import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { fr } from './locales/fr'
import { en } from './locales/en'
import type { Language, Translations, TranslationObjectType } from './types'
import { usePatch } from '../utils/apiComponent/useApi'
import { endpoints } from '../utils/endpoints'
import { tokenManager } from '../utils/apiComponent/apiClient'

const translations = {
  fr,
  en
} as const

interface TranslationContextType {
  language: Language
  t: (key: string, params?: Record<string, string | number>) => string
  tObj: TranslationObjectType
  changeLanguage: (lang: Language) => void
}

export const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

export function TranslationProvider({ children }: { children: React.ReactNode }) {
  // Récupérer la langue depuis localStorage ou utiliser 'fr' par défaut
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('app_language') as Language
    return savedLanguage && (savedLanguage === 'fr' || savedLanguage === 'en') ? savedLanguage : 'fr'
  })
  
  // Hook pour mettre à jour la langue préférée via l'API
  const { execute: updatePreferredLanguage } = usePatch(endpoints.updatePreferredLanguage)

  // Fonction de traduction classique (string)
  const t = useCallback((key: string, params: Record<string, string | number> = {}): string => {
    const keys = key.split('.')
    let value: any = translations[language]

    // Naviguer dans l'objet de traduction
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        // Si la clé n'existe pas, retourner la clé elle-même
        console.warn(`Translation key not found: ${key}`)
        return key
      }
    }

    // Si c'est une chaîne, remplacer les paramètres si présents
    if (typeof value === 'string') {
      return Object.keys(params).reduce((str, paramKey) => {
        return str.replace(`{{${paramKey}}}`, String(params[paramKey]))
      }, value)
    }

    return value || key
  }, [language])

  // Créer un objet de traductions accessible via propriétés (ex: tObj.nav.home())
  const tObj = useMemo(() => {
    return createTranslationObjectWithKeys(t, translations[language] as Translations)
  }, [language, t])

  // Changer la langue
  const changeLanguage = useCallback(async (lang: Language) => {
    if (!translations[lang]) {
      console.warn(`Language ${lang} is not supported`)
      return
    }
    
    // Mettre à jour l'état local immédiatement
    setLanguage(lang)
    localStorage.setItem('app_language', lang)
    
    // Si l'utilisateur est connecté, sauvegarder la préférence sur le serveur
    const token = tokenManager.getToken()
    if (token) {
      try {
        await updatePreferredLanguage({ preferredLanguage: lang })
        // Mettre à jour les données utilisateur dans localStorage si disponibles
        const userData = localStorage.getItem('userData')
        if (userData) {
          try {
            const user = JSON.parse(userData)
            user.preferredLanguage = lang
            localStorage.setItem('userData', JSON.stringify(user))
          } catch (e) {
            console.error('Error updating user data:', e)
          }
        }
      } catch (error) {
        console.error('Error updating preferred language:', error)
        // Ne pas bloquer le changement de langue même si l'API échoue
      }
    }
    
    // Recharger la page pour appliquer les traductions
    window.location.reload()
  }, [updatePreferredLanguage])

  // Charger la langue préférée depuis l'API si l'utilisateur est connecté
  useEffect(() => {
    const token = tokenManager.getToken()
    if (token) {
      // Vérifier si l'utilisateur a une langue préférée sauvegardée
      const userData = localStorage.getItem('userData')
      if (userData) {
        try {
          const user = JSON.parse(userData)
          if (user.preferredLanguage && (user.preferredLanguage === 'fr' || user.preferredLanguage === 'en')) {
            setLanguage(user.preferredLanguage)
            localStorage.setItem('app_language', user.preferredLanguage)
            return
          }
        } catch (e) {
          console.error('Error parsing user data:', e)
        }
      }
    }
    
    // Si pas de langue préférée depuis l'API, utiliser localStorage ou langue du navigateur
    const savedLanguage = localStorage.getItem('app_language')
    if (savedLanguage && (savedLanguage === 'fr' || savedLanguage === 'en')) {
      setLanguage(savedLanguage)
    } else {
      const browserLang = navigator.language.split('-')[0] as Language
      if (translations[browserLang]) {
        setLanguage(browserLang)
        localStorage.setItem('app_language', browserLang)
      }
    }
  }, [])

  return (
    <TranslationContext.Provider value={{ language, t, tObj, changeLanguage }}>
      {children}
    </TranslationContext.Provider>
  )
}

export function useTranslation() {
  const context = useContext(TranslationContext)
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider')
  }
  return context
}

// Fonction helper pour créer l'objet de traduction avec les clés complètes
function createTranslationObjectWithKeys(
  t: (key: string, params?: Record<string, string | number>) => string,
  translationsObj: Record<string, any>,
  prefix = ''
): any {
  const result: any = {}
  
  for (const key in translationsObj) {
    const fullKey = prefix ? `${prefix}.${key}` : key
    const value = translationsObj[key]
    
    if (typeof value === 'string') {
      // Créer une fonction qui appelle t avec la clé complète
      result[key] = (params?: Record<string, string | number>) => {
        return t(fullKey, params)
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = createTranslationObjectWithKeys(t, value, fullKey)
    }
  }
  
  return result
}

